import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionIcon, Button, Drawer, Group, Loader, NumberInput, Paper, Select, Stack,
  Table, Text, Tooltip,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconChartHistogram, IconPlus, IconTrash } from '@tabler/icons-react';
import { isAxiosError } from 'axios';
import { PageHeader } from '../../components/PageHeader';
import { EmptyState } from '../../components/EmptyState';
import { useAccountingPeriods } from './periodsApi';
import { useAccounts } from './accountsApi';
import { useCostCenters } from './costCentersApi';
import { useBudgets, useCreateBudget, useDeleteBudget } from './budgetsApi';

const fmt = (n: number) => new Intl.NumberFormat('ar-PS', { minimumFractionDigits: 2, numberingSystem: 'latn' }).format(n);

export function BudgetsPage() {
  const { t, i18n } = useTranslation();
  const periods = useAccountingPeriods();
  const accounts = useAccounts(false);
  const costCenters = useCostCenters();
  const [periodId, setPeriodId] = useState<string | null>(null);
  const { data: budgets, isLoading } = useBudgets(periodId);
  const create = useCreateBudget();
  const del = useDeleteBudget();
  const [drawer, setDrawer] = useState(false);

  const nameOf = (ar: string, en: string | null) => (i18n.language === 'ar' ? ar : (en ?? ar));

  const periodOptions = (periods.data ?? []).map((p) => ({ value: p.accountingPeriodId, label: nameOf(p.nameAr, p.nameEn) }));
  const accountOptions = (accounts.data ?? []).filter((a) => a.isPostable)
    .map((a) => ({ value: a.accountId, label: `${a.code} — ${nameOf(a.nameAr, a.nameEn)}` }));
  const ccOptions = (costCenters.data ?? []).filter((c) => !c.isGroup)
    .map((c) => ({ value: c.costCenterId, label: `${c.code} — ${nameOf(c.nameAr, c.nameEn)}` }));

  // Default to the first period once loaded.
  useEffect(() => {
    if (!periodId && periodOptions.length) setPeriodId(periodOptions[0]!.value);
  }, [periodOptions, periodId]);

  const form = useForm({
    initialValues: { accountId: '', costCenterId: null as string | null, amount: 0 },
    validate: { accountId: (v) => (v ? null : t('common.errors.required')) },
  });

  const submit = form.onSubmit(async (v) => {
    if (!periodId) return;
    try {
      await create.mutateAsync({ accountingPeriodId: periodId, accountId: v.accountId, costCenterId: v.costCenterId, amount: v.amount });
      notifications.show({ color: 'green', title: t('common.toasts.saved'), message: '' });
      setDrawer(false);
      form.reset();
    } catch (err) {
      notifications.show({ color: 'red', title: t('common.toasts.errorTitle'),
        message: (isAxiosError(err) && (err.response?.data?.detail as string)) || t('common.toasts.errorBody') });
    }
  });

  const rows = budgets ?? [];

  return (
    <Stack gap="lg">
      <PageHeader
        eyebrow={t('nav.sections.accounting')}
        title={t('accounting.budgets.title')}
        subtitle={t('accounting.budgets.subtitle')}
        actions={
          <Button leftSection={<IconPlus size={16} stroke={1.8} />} disabled={!periodId} onClick={() => { form.reset(); setDrawer(true); }}>
            {t('accounting.budgets.create')}
          </Button>
        }
      />

      <Paper p="md">
        <Select label={t('accounting.budgets.period')} data={periodOptions} value={periodId}
          onChange={setPeriodId} searchable w={280} placeholder={t('accounting.budgets.selectPeriod')} />
      </Paper>

      <Paper p={rows.length ? 0 : 'lg'}>
        {!periodId ? (
          <EmptyState icon={<IconChartHistogram size={22} stroke={1.6} />} title={t('accounting.budgets.selectPeriod')} description={t('accounting.budgets.selectPeriodBody')} />
        ) : isLoading ? (
          <Stack align="center" py={48}><Loader size="sm" /></Stack>
        ) : rows.length === 0 ? (
          <EmptyState icon={<IconChartHistogram size={22} stroke={1.6} />} title={t('accounting.budgets.empty')}
            description={t('accounting.budgets.emptyBody')} actionLabel={t('accounting.budgets.create')} onAction={() => { form.reset(); setDrawer(true); }} />
        ) : (
          <Table verticalSpacing="md" highlightOnHover striped="even">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>{t('accounting.budgets.account')}</Table.Th>
                <Table.Th>{t('accounting.budgets.costCenter')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.budgets.budget')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.budgets.actual')}</Table.Th>
                <Table.Th style={{ textAlign: 'end' }}>{t('accounting.budgets.variance')}</Table.Th>
                <Table.Th style={{ width: 60, textAlign: 'end' }}>{t('common.actions')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((b) => (
                <Table.Tr key={b.budgetId}>
                  <Table.Td>
                    <Text size="sm"><span className="font-numeric font-mono text-xs text-[var(--wheels-ink-muted)]">{b.accountCode}</span> {nameOf(b.accountNameAr, b.accountNameEn)}</Text>
                  </Table.Td>
                  <Table.Td c="var(--wheels-ink-muted)">{b.costCenterName ?? '—'}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{fmt(b.amount)}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>{fmt(b.actual)}</Table.Td>
                  <Table.Td className="font-numeric" style={{ textAlign: 'end' }}>
                    <Text size="sm" fw={600} c={b.variance >= 0 ? 'green' : 'red'}>{fmt(b.variance)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Group justify="flex-end">
                      <Tooltip label={t('common.delete')} withinPortal>
                        <ActionIcon variant="subtle" color="red" onClick={() => del.mutate(b.budgetId)} aria-label={t('common.delete')}>
                          <IconTrash size={16} stroke={1.6} />
                        </ActionIcon>
                      </Tooltip>
                    </Group>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Paper>

      <Drawer opened={drawer} onClose={() => setDrawer(false)} position={i18n.language === 'ar' ? 'left' : 'right'}
        size="md" padding="lg" title={t('accounting.budgets.create')}>
        <form onSubmit={submit}>
          <Stack gap="md">
            <Select label={t('accounting.budgets.account')} data={accountOptions} searchable withAsterisk {...form.getInputProps('accountId')} />
            <Select label={t('accounting.budgets.costCenter')} data={ccOptions} searchable clearable {...form.getInputProps('costCenterId')} />
            <NumberInput label={t('accounting.budgets.budget')} min={0} decimalScale={2} {...form.getInputProps('amount')} />
            <Group justify="flex-end" gap="sm" mt="sm">
              <Button variant="default" onClick={() => setDrawer(false)}>{t('common.cancel')}</Button>
              <Button type="submit" loading={create.isPending}>{t('common.create')}</Button>
            </Group>
          </Stack>
        </form>
      </Drawer>
    </Stack>
  );
}
